import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../utils/logger';
import { useEvidenceConfig } from './useEvidenceConfig';

const logger = createLogger({ module: 'useAchievements' });

export interface Achievement {
  id: string;
  focus_id: string;
  user_id: string;
  title: string;
  situation: string;
  task: string;
  action: string;
  result: string;
  achievement_date: string;
  category?: string;
  is_featured: boolean;
  impact_score?: number;
  key_skills?: string[];
  created_at: string;
  updated_at: string;
}

interface UseAchievementsReturn {
  achievements: Achievement[];
  featuredAchievements: Achievement[];
  loading: boolean;
  error: string | null;
  createAchievement: (achievement: Omit<Achievement, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<Achievement | null>;
  updateAchievement: (id: string, updates: Partial<Achievement>) => Promise<boolean>;
  deleteAchievement: (id: string) => Promise<boolean>;
  toggleFeatured: (id: string) => Promise<boolean>;
  refreshAchievements: () => Promise<void>;
}

export const useAchievements = (focusId?: string | null): UseAchievementsReturn => {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { config } = useEvidenceConfig(focusId || '');

  // Fetch achievements
  const fetchAchievements = useCallback(async () => {
    if (!user || !focusId) {
      setAchievements([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('review_achievements')
        .select('*')
        .eq('focus_id', focusId)
        .order('achievement_date', { ascending: false });

      if (fetchError) throw fetchError;

      setAchievements(data || []);
    } catch (err) {
      logger.error('Failed to fetch achievements', { error: err });
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  }, [user, focusId]);

  // Create achievement
  const createAchievement = useCallback(async (
    achievement: Omit<Achievement, 'id' | 'user_id' | 'created_at' | 'updated_at'>
  ): Promise<Achievement | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('review_achievements')
        .insert({
          ...achievement,
          user_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setAchievements(prev => [data, ...prev]);
      
      return data;
    } catch (err) {
      logger.error('Failed to create achievement', { error: err });
      setError('Failed to create achievement');
      return null;
    }
  }, [user]);

  // Update achievement
  const updateAchievement = useCallback(async (
    id: string,
    updates: Partial<Achievement>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('review_achievements')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setAchievements(prev => prev.map(a => 
        a.id === id ? { ...a, ...updates } : a
      ));

      return true;
    } catch (err) {
      logger.error('Failed to update achievement', { error: err });
      setError('Failed to update achievement');
      return false;
    }
  }, [user]);

  // Delete achievement
  const deleteAchievement = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('review_achievements')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setAchievements(prev => prev.filter(a => a.id !== id));

      return true;
    } catch (err) {
      logger.error('Failed to delete achievement', { error: err });
      setError('Failed to delete achievement');
      return false;
    }
  }, [user]);

  // Toggle featured status
  const toggleFeatured = useCallback(async (id: string): Promise<boolean> => {
    if (!user) return false;

    const achievement = achievements.find(a => a.id === id);
    if (!achievement) return false;

    // Check featured limit (use configurable limit)
    const featuredLimit = config.displayLimits?.featured_achievements_max || 5;
    const currentFeaturedCount = achievements.filter(a => a.is_featured).length;
    if (!achievement.is_featured && currentFeaturedCount >= featuredLimit) {
      setError(`You can only feature up to ${featuredLimit} achievements`);
      return false;
    }

    return updateAchievement(id, { is_featured: !achievement.is_featured });
  }, [user, achievements, updateAchievement]);

  // Get featured achievements
  const featuredAchievements = achievements.filter(a => a.is_featured);

  // Refresh function
  const refreshAchievements = useCallback(() => {
    return fetchAchievements();
  }, [fetchAchievements]);

  // Initial fetch
  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  return {
    achievements,
    featuredAchievements,
    loading,
    error,
    createAchievement,
    updateAchievement,
    deleteAchievement,
    toggleFeatured,
    refreshAchievements
  };
};